import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import UploadAdCreativeCampaignForm from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm'
import NewCampaignBudgetFields from './NewCampaignFields/NewCampaignBudgetFields'
import NewCampaignImpressionsFields from './NewCampaignFields/NewCampaignImpressionsFields'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import useRightBudgetAndImpressionsCalculation from '../../../../Multiplatform/UploadAdCreative/useRightBudgetAndImpressionsCalculation'

import { formatOptionsList } from '../../../../../features/formatters'

import { setRightUploadAdData } from '../../../../../modules/actions/rights'
import { getLineItems } from '../../../../../modules/actions/lineItems'
import { createCampaign } from '../../../../../modules/actions/campaigns'
import { combinedCampaignsSelector } from '../../../../../modules/selectors/combinedData'
import { rightSelector } from '../../../../../modules/selectors/rights'
import { selectedTikTokAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import { lineItemsLoadingSelector } from '../../../../../modules/selectors/lineItems'
import { campaignCreateIsLoadingSelector } from '../../../../../modules/selectors/campaigns'
import { selectedPartnershipSelector } from '../../../../../modules/selectors/partnerships'

import {
  DIGITAL_ALLOCATION_TYPE_BUDGET,
  DIGITAL_ALLOCATION_TYPE_IMPRESSIONS
} from '../../../../Multiplatform/Partnerships/RightForms/fields'
import { TIKTOK_PLATFORM, TIKTOK_PROVIDER_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { CAMPAIGN } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/fields'
import { LIFETIME_DATE_PRESET } from '../../../../../constants/selectLists/listItemFiltersList'
import { getInitialValues, getValidationSchema, transformValuesToBE } from './fields'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'
import { BUDGET_DAILY, BUDGET_LIFETIME, OBJECTIVE } from '../../../../Multiplatform/CampaignForms/fields'
import { IMPRESSIONS } from '../../../../Multiplatform/UploadAdCreative/fields'

const campaignStepValidationFields = [OBJECTIVE, IMPRESSIONS, BUDGET_LIFETIME, BUDGET_DAILY]

const CampaignForm = ({ formProgressItems, onSuccessSubmit }) => {
  const dispatch = useDispatch()

  const combinedCampaigns = useSelector(combinedCampaignsSelector)
  const right = useSelector(rightSelector)
  const { id: selectedTikTokAdAccountId, currency_symbol: currencySymbol } = useSelector(
    selectedTikTokAdAccountSelector
  )
  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const { allocation_type: rightAllocationType } = right

  const { campaigns = [] } = combinedCampaigns

  const isBudgetAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_BUDGET
  const isImpressionAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS

  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()

  const tikTokCampaigns = useMemo(
    () => campaigns.filter(campaign => campaign.provider === TIKTOK_PROVIDER_PLATFORM),
    [campaigns]
  )

  const isCampaignsExist = !!tikTokCampaigns.length

  const formattedCampaigns = formatOptionsList({
    list: tikTokCampaigns,
    valueName: 'id',
    labelName: 'name'
  })

  const onSubmit = useCallback(
    values => {
      // if existing campaign is selected, find full data and set to redux state + fetch line items
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        const selectedCampaignFullData = formattedCampaigns.find(campaign => values[CAMPAIGN] === campaign.id)

        dispatch(setRightUploadAdData({ campaign: selectedCampaignFullData }, TIKTOK_PLATFORM))

        dispatch(
          getLineItems(
            {
              account: selectedTikTokAdAccountId,
              campaign_id: values[CAMPAIGN],
              date_preset: LIFETIME_DATE_PRESET,
              insights: ['impressions']
            },
            TIKTOK_PLATFORM
          )
        )
      } else {
        const campaignData = transformValuesToBE({
          values,
          adAccountId: selectedTikTokAdAccountId,
          right,
          selectedPartnership
        })

        dispatch(
          createCampaign({ campaignData }, TIKTOK_PLATFORM, {
            pushToCombinedCampaignsList: true
          })
        )
      }
    },
    [dispatch, formattedCampaigns, right, selectedTikTokAdAccountId, selectedPartnership]
  )

  const formik = useFormik({
    initialValues: getInitialValues({ isCampaignsExist, rightAllocationType }),
    validationSchema: getValidationSchema({
      remainingBudget,
      remainingImpressions,
      currencySymbol,
      allocationTypeImpressions: isImpressionAllocationType
    }),
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik

  const { [LIST_ITEM_OPTION]: campaignOption } = values

  // when we use existing campaign, we need to pre-load line items for the next LineItem form
  // in this case submit form loads line item. However, we don't do the same for error selectors,
  // because we don't block user for accessing next form when line items get request failed
  const isLoadingSelector =
    campaignOption === OPTION_EXISTING_LIST_ITEM ? lineItemsLoadingSelector : campaignCreateIsLoadingSelector

  return (
    <UploadAdCreativeCampaignForm
      adAccountId={selectedTikTokAdAccountId}
      formik={formik}
      platform={TIKTOK_PLATFORM}
      onSuccessSubmit={onSuccessSubmit}
      isLoadingSelector={isLoadingSelector}
      existedCampaigns={formattedCampaigns}
      formProgressItems={formProgressItems}
      campaignStepValidationFields={campaignStepValidationFields}
    >
      {isBudgetAllocationType && <NewCampaignBudgetFields formik={formik} remainingBudget={remainingBudget} />}
      {isImpressionAllocationType && (
        <NewCampaignImpressionsFields formik={formik} remainingImpressions={remainingImpressions} />
      )}
    </UploadAdCreativeCampaignForm>
  )
}

export default CampaignForm
